import ReactSelect, { MultiValue, SingleValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { colors } from 'ui/theme/colors';
import { isFunction } from 'utils/helpers';
import { MultiSelectInput } from './MultiSelectInput';
import { MultiSelectInputOption } from './MultiSelectInputOption';
import { MultiSelectMultiValueRemove } from './MultiSelectMultiValueRemove';
import { SelectClearIndicator } from './SelectClearIndicator';
import { SelectContainer } from './SelectContainer';
import { SelectDropdownIndicator } from './SelectDropdownIndicator';
import { SelectErrorMessage } from './SelectErrorMessage';
import { SelectInput } from './SelectInput';
import { SelectLoadingIndicator } from './SelectLoadingIndicator';
import { SelectMenuList } from './SelectMenuList';
import { SelectValueContainer } from './SelectValueContainer';
import { SingleSelectInputOption } from './SingleSelectInputOption';
import { DefaultTheme } from './Themes';
import { BaseOption, SelectProps } from './types';
import {
  addCreatableProps,
  getCompactMultiValueContainer,
  groupOptions,
  parseSelectAlls,
} from './utils';
import { VirtualizedSelectMenuList } from './VirtualizedSelectMenuList';

export const Select = ({
  options,
  onSelect,
  categoryLabels,
  value,
  defaultValue,
  optionLimit,
  errorMsg,
  compactCutoff,
  inputId = '',
  id,
  isMulti,
  usePortal = false,
  isVirtualized = false,
  showCategoryDivider = false,
  autoFocus = false,
  isDisabled = false,
  isLoading = false,
  canCreate = false,
  createPrefix = 'Create',
  createSuffix = '',
  isMenuOpen,
  isDefaultMenuOpen = false,
  hideSelectedOptions = false,
  hideDropdownIndicator = false,
  closeMenuOnSelect = false,
  customTheme = DefaultTheme,
  onCreateOption,
  placeholder = 'Search or select an item...',
  isOptionDisabled,
  isClearable = false,
  shouldHideDisplayValues = false,
  filterOption,
  Input,
  MultiValue,
  menuPortalTarget,
  ...props
}: SelectProps) => {
  const parsedOptions =
    categoryLabels && Object.keys(categoryLabels)?.length > 0
      ? groupOptions(
          options,
          categoryLabels,
          (value as BaseOption[]) || [],
          optionLimit,
          isMulti,
          showCategoryDivider,
        )
      : options;
  const valueArr = Object.values(value ?? []).map((val: BaseOption) => val?.value);
  const MultiValueContainer = getCompactMultiValueContainer(compactCutoff);
  const SelectComponent = canCreate ? CreatableSelect : ReactSelect;
  const createableProps = addCreatableProps(
    canCreate,
    createPrefix,
    createSuffix,
    isMulti,
    onSelect,
    onCreateOption,
  );

  /** adding customProps requires module augmentation for the base select component to satisfy typescript.
   *  See: https://react-select.com/typescript#custom-select-props
   *  For now custom props can be type ignored
   */
  const customSelectProps = { optionLimit, canCreate }; // add more if needed
  const useParseSelectAlls = isMulti && categoryLabels && Object.keys(categoryLabels)?.length > 0;

  return (
    <>
      <SelectComponent
        id={id}
        autoFocus={autoFocus}
        menuIsOpen={isMenuOpen}
        controlShouldRenderValue={!shouldHideDisplayValues}
        defaultMenuIsOpen={isDefaultMenuOpen}
        inputId={inputId}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isOptionDisabled={(option) => {
          const customIsOptionDisabled = isFunction(isOptionDisabled) && isOptionDisabled(option);

          const optionLimitReached = !!(
            optionLimit &&
            !valueArr.includes(option.value) &&
            (value?.length ?? 0) >= optionLimit
          );

          return customIsOptionDisabled || optionLimitReached;
        }}
        styles={customTheme(errorMsg)}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary25: colors.grey15,
            primary: colors.borderInteractive,
          },
        })}
        placeholder={placeholder}
        onChange={(option: SingleValue<BaseOption> | MultiValue<BaseOption>) => {
          const isOptionArray = Array.isArray(option);
          if (isOptionArray && useParseSelectAlls) {
            parseSelectAlls(option, onSelect, parsedOptions);
          } else {
            const selectedOption = isOptionArray ? option : [option];
            onSelect(selectedOption);
          }
        }}
        components={{
          Input: Input || (isMulti ? MultiSelectInput : SelectInput),
          Option: isMulti ? MultiSelectInputOption : SingleSelectInputOption,
          ClearIndicator: SelectClearIndicator,
          DropdownIndicator: hideDropdownIndicator ? () => null : SelectDropdownIndicator,
          MultiValueRemove: MultiSelectMultiValueRemove,
          LoadingIndicator: SelectLoadingIndicator,
          SelectContainer,
          MultiValueContainer,
          ValueContainer: SelectValueContainer,
          MenuList: isVirtualized ? VirtualizedSelectMenuList : SelectMenuList,
          /**
           * Optional custom components
           */
          ...(MultiValue && { MultiValue }),
        }}
        menuPortalTarget={menuPortalTarget || usePortal ? document.body : null}
        isMulti={isMulti}
        hideSelectedOptions={hideSelectedOptions}
        closeMenuOnSelect={!isMulti || closeMenuOnSelect}
        tabSelectsValue={false}
        options={parsedOptions}
        value={value}
        defaultValue={defaultValue}
        isClearable={isClearable}
        filterOption={filterOption}
        {...createableProps}
        {...customSelectProps}
        {...props}
      />
      {errorMsg && <SelectErrorMessage>{errorMsg}</SelectErrorMessage>}
    </>
  );
};
